import { useEffect, useState, useCallback, useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { api, ApiError, hasLocalSession as hasLocalSessionNow } from "../api/client";
import { loginRequest } from "./msalConfig";

/** The refusals of backend/src/lib/signInPolicy.ts; each arrives with a sentence in `message`. */
const SIGN_IN_POLICY_CODES = new Set(["sso_required", "provider_not_allowed", "personal_account", "tenant_not_allowed"]);

/**
 * Every component that calls useCurrentUser() holds its own copy of the user. A page that changes
 * something about the signed-in person (their display name) announces it here so the others, such
 * as the top bar, show it without a reload.
 */
export const USER_PATCHED_EVENT = "abacas:user-patched";
export function announceUserPatch(patch: Partial<Pick<CurrentUser, "displayName" | "language">>): void {
  window.dispatchEvent(new CustomEvent(USER_PATCHED_EVENT, { detail: patch }));
}

export interface CurrentUser {
  id: string;
  role: "client" | "consultant" | "auditor";
  organizationId: string | null;
  bureauId: string | null;
  consultancyId: string | null;
  isPlatformOwner: boolean;
  isOrgAdmin: boolean;
  isBureauAdmin: boolean;
  onboardingComplete: boolean;
  // Password accounts: sign in with email + password + authenticator app.
  // `mfaEnrolmentRequired` means the session is restricted to enrolment.
  hasLocalPassword: boolean;
  totpEnabled: boolean;
  recoveryCodesLeft: number;
  mfaEnrolmentRequired: boolean;
  displayName: string;
  email: string;
  language: string | null;
  // Set for consultant users who hold auditor accreditation and have been granted access.
  canActAsAuditor: boolean;
  auditorBureauId: string | null;
  // A consultant working inside their consultancy's own organisation as its
  // client (POST /auth/select-role { role: "client" }).
  actingAsOwnOrg?: boolean;
  organizationName?: string | null;
  // A platform administrator viewing a demo organisation as this member
  // (POST /auth/view-as-persona). Every other field is the member's.
  viewedBy?: { id: string; displayName: string } | null;
  // Client users: the org-defined role and the permission set the API resolved
  // for it. Gate UI on `permissions`, not on the role name.
  orgRole: { id: string; name: string; isSystem: boolean; builtinKey: string | null } | null;
  permissions: string[];
  // Client users whose organisation is in a group. group:view / group:admin in
  // `permissions` (parent organisation only) unlock the switcher and overview.
  group?: { id: string; name: string; parentOrganizationId: string; organizationIds: string[] } | null;
  // Sub-roles of consultancy staff and bureau members (present in /auth/me;
  // optional so older payloads still type-check). For framework specialists,
  // frameworkAccess lists the standards a bureau admin has granted
  // (empty = nothing until granted).
  consultantRole?: string | null;
  auditorRole?: string | null;
  frameworkAccess?: string[];
  // Auditor sessions: the bureau's display name, for the user menu and header.
  bureauName?: string | null;
  // Client users: the product their organisation is on (backend/src/lib/editions.ts) and
  // the frontend that serves it — the main platform and jsy.abacassecurity.com each send a
  // member of the other edition to their own site.
  edition?: "standard" | "jersey" | null;
  editionUrl?: string | null;
  // Consultancy staff: whether their firm may invite Jersey edition clients (platform owner or licensee).
  canInviteJerseyEdition?: boolean;
}

// Retry delays between /auth/me attempts on transient failures (ms).
// Two retries: wait 1 s then 2 s before giving up.
const RETRY_DELAYS = [1000, 2000];

/**
 * Resolves the signed-in user from the API (never trusts anything decoded
 * client-side from a token) — for both Entra ID sessions and local
 * auditor/bureau sessions. `organisationNotRegistered` distinguishes "not
 * logged in" from "this Microsoft 365 tenant has no organisation set up
 * yet", which the frontend routes to the registration screen instead of a
 * generic error.
 *
 * Transient failures (backend cold-start, MSAL token-acquisition race,
 * temporary network blip) are retried up to twice before the error screen
 * is shown, so users rarely see "Could not load your account" for
 * recoverable issues.
 */
export function useCurrentUser() {
  const isEntraAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [organisationNotRegistered, setOrganisationNotRegistered] = useState(false);
  // A Microsoft sign-in the account's sign-in policy refuses (backend/src/lib/signInPolicy.ts):
  // a personal account, an unapproved tenant, Microsoft switched off. Retrying cannot help.
  const [signInRefused, setSignInRefused] = useState<string | null>(null);

  const hasLocalSession = hasLocalSessionNow();

  // Incremented on every refetch() call. Allows in-flight retries to detect
  // that a newer fetch has started and bail out without updating stale state.
  const fetchGen = useRef(0);

  const refetch = useCallback(async () => {
    // Wait for MSAL to finish processing a redirect before attempting token
    // acquisition — calling acquireTokenSilent mid-redirect causes it to fail,
    // triggering the popup fallback which can hang indefinitely.
    if (inProgress === InteractionStatus.HandleRedirect) return;

    // Read at call time, not from the render that created this callback: a sign-in that
    // has just installed a session (Login's onLocalLogin) calls this before any re-render.
    if (!isEntraAuthenticated && !hasLocalSessionNow()) {
      setUser(null);
      setLoading(false);
      return;
    }

    const gen = ++fetchGen.current;
    setLoading(true);

    const delays = [...RETRY_DELAYS];

    for (;;) {
      if (gen !== fetchGen.current) return; // superseded by a newer refetch call

      try {
        const data = await api.get<{ user: CurrentUser }>("/auth/me");
        if (gen !== fetchGen.current) return;
        setUser(data.user);
        setOrganisationNotRegistered(false);
        setSignInRefused(null);
        setLoading(false);
        return;
      } catch (e) {
        if (gen !== fetchGen.current) return;

        // org_not_registered means the user's tenant isn't provisioned — no point
        // retrying; route to the appropriate registration/contact screen.
        if (
          e instanceof ApiError &&
          e.payload &&
          (e.payload as { error?: string }).error === "organisation_not_registered"
        ) {
          setOrganisationNotRegistered(true);
          setUser(null);
          setLoading(false);
          return;
        }
        if (e instanceof ApiError && e.status === 403 && e.code && SIGN_IN_POLICY_CODES.has(e.code)) {
          setSignInRefused(e.message);
          setUser(null);
          setLoading(false);
          return;
        }

        const delay = delays.shift();
        if (delay !== undefined) {
          // Transient failure — wait then retry.
          await new Promise<void>(r => setTimeout(r, delay));
          continue;
        }

        // All retries exhausted.
        // For a pure Entra session that keeps returning 401, the MSAL token
        // acquisition failed silently (e.g. acquireTokenSilent threw a
        // BrowserAuthError and returned null). Force a loginRedirect so the
        // user gets a fresh token rather than seeing an unhelpful error screen.
        const isPureEntraSession = isEntraAuthenticated && !hasLocalSession;
        if (e instanceof ApiError && e.status === 401 && isPureEntraSession) {
          instance.loginRedirect(loginRequest);
          return; // page navigates away
        }

        setUser(null);
        setLoading(false);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEntraAuthenticated, hasLocalSession, inProgress, instance]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const onPatched = (e: Event) => {
      const patch = (e as CustomEvent<Partial<CurrentUser>>).detail;
      setUser((prev) => (prev ? { ...prev, ...patch } : prev));
    };
    window.addEventListener(USER_PATCHED_EVENT, onPatched);
    return () => window.removeEventListener(USER_PATCHED_EVENT, onPatched);
  }, []);

  return { user, loading, organisationNotRegistered, signInRefused, refetch };
}
